/**
 * Everything Isidora says in words, in every language it says it in.
 *
 * Isidora, as Marco Polo tells it — Invisible Cities, "Cities & Memory · 2".
 *
 * Fragments are pinned to positions on the stair. The overlay shows exactly
 * one at a time and crossfades at the boundary, so the text is read by
 * walking rather than by scrolling a column of prose.
 *
 * `at` is the journey position where the fragment takes over, and it is
 * shared by every language: the stair is cut to the passage, so a translation
 * may change the words but never where they fall. Each other key is a
 * language tag. Adding one is described in docs/EXTENDING.md.
 */

/** Every language the city can be read in, in the order the toggle cycles. */
export const LANGS = ['zh', 'en'];

/** What the city opens in when nothing has been asked for. */
export const DEFAULT_LANG = 'zh';

/** Shown on the language glyph: what one more press would give you. */
export const LANG_GLYPH = { zh: 'EN', en: '中' };

export const PASSAGE = [
  {
    at: 0.0,
    en: 'To the man who rides long through savage lands,',
    zh: '當一個人在蠻荒之地長久騎行，',
  },
  { at: 0.055, en: 'the desire for a city overcomes him.', zh: '對一座城市的渴望便攫住了他。' },
  { at: 0.13, en: 'At last he comes to Isidora —', zh: '最後他來到伊西多拉——' },
  {
    at: 0.2,
    en: 'a city where the buildings have spiral staircases',
    zh: '一座城裡的樓房都有螺旋的階梯，',
  },
  { at: 0.27, en: 'encrusted with spiral seashells,', zh: '階梯上密布著螺旋的貝殼，' },
  {
    at: 0.35,
    en: 'where perfect telescopes and violins are made,',
    zh: '這裡製造完美的望遠鏡與小提琴，',
  },
  {
    at: 0.45,
    en: 'where the foreigner hesitating between two women',
    zh: '這裡的異鄉人在兩個女子之間猶豫時，',
  },
  { at: 0.51, en: 'always encounters a third,', zh: '總會遇見第三個，' },
  {
    at: 0.59,
    en: 'where cockfights degenerate into bloody brawls',
    zh: '這裡的鬥雞總淪為賭徒之間',
  },
  { at: 0.645, en: 'among the bettors.', zh: '血腥的鬥毆。' },
  {
    at: 0.71,
    en: 'He was thinking of all these things when he desired a city.',
    zh: '他渴望一座城市時，想的正是這一切。',
  },
  {
    at: 0.79,
    en: 'Isidora, therefore, is the city of his dreams:',
    zh: '所以，伊西多拉就是他夢中的城：',
  },
  { at: 0.835, en: 'with one difference.', zh: '只有一點不同。' },
  {
    at: 0.875,
    en: 'The dreamed-of city contained him as a young man;',
    zh: '夢裡的城容納的是年輕的他；',
  },
  { at: 0.915, en: 'he arrives at Isidora in his old age.', zh: '而他抵達伊西多拉時已經年老。' },
  {
    at: 0.95,
    en: 'In the square there is a wall where the old men sit',
    zh: '廣場上有一道牆，老人們坐在那裡',
  },
  { at: 0.972, en: 'and watch the youth go by;', zh: '看著年輕人走過；' },
  { at: 0.988, en: 'he is seated in a row with them.', zh: '他與他們並肩坐成一排。' },
  { at: 0.999, en: 'Desires are already memories.', zh: '慾望已經是記憶。', final: true },
];

/** Shown once, under the first line, then never again. */
export const INVITATION = {
  en: 'scroll, or drag, to descend · hold a gaze to remember',
  zh: '滾動或拖曳以下行 · 凝視片刻即成記憶',
};

/** Whispered by the overlay when a thing turns from desire into memory. */
export const CONVERSIONS = {
  en: {
    shell: ['a shell, kept', 'the sound of a sea you never reached', 'held, and already past'],
    telescope: ['a distance, folded into you', 'what you saw is now what you had'],
    violin: ['a note you will keep humming', 'the string stops; the room does not'],
  },
  zh: {
    shell: ['一枚貝殼，留下了', '一片你從未抵達的海的聲音', '握住了，也已經過去'],
    telescope: ['一段距離，摺進了你', '你所見的，如今是你曾有的'],
    violin: ['一個你會一直哼下去的音', '弦停了，房間沒有'],
  },
};

/** Shown when the traveller lifts a telescope to the eye. */
export const TELESCOPE_CAPTION = {
  en: 'the city as it was desired',
  zh: '被渴望時的那座城',
};

/**
 * The handful of words that are not Calvino's: the veil, the labels on the
 * four glyphs, and the card that hands the city to a phone.
 */
export const UI = {
  en: {
    docTitle: 'Isidora',
    title: 'ISIDORA',
    subtitle: 'invisible cities · cities & memory · 2',
    sound: 'sound',
    inspect: 'inspector',
    language: '中文',
    share: 'open on a phone',
    shareTitle: 'carry it with you',
    shareHint: 'point a phone camera at this',
    shareNetwork: 'the phone must be on the same network as this computer',
    shareLocal: 'this page is only served to this computer — start it with `pnpm dev` and it will offer an address the phone can reach',
    shareOther: 'another address',
    shareClose: 'close',
    noWebgl: 'Isidora needs WebGL, and this browser has not any.',
    keys: ['i · inspector    space/hold · telescope', 'wheel, drag, ↑↓ · descend    home/end', 'm · sound    l · 中文    q · phone'],
  },
  zh: {
    docTitle: '伊西多拉 · Isidora',
    title: '伊西多拉',
    subtitle: '看不見的城市 · 城市與記憶 · 2',
    sound: '聲音',
    inspect: '檢視',
    language: 'English',
    share: '在手機上開啟',
    shareTitle: '把這座城帶走',
    shareHint: '用手機相機對準這裡',
    shareNetwork: '手機需與這台電腦在同一個網路',
    shareLocal: '這個網頁目前只供本機開啟 — 以 `pnpm dev` 啟動後，就會提供手機可連的位址',
    shareOther: '換一個位址',
    shareClose: '關閉',
    noWebgl: '伊西多拉需要 WebGL，而這個瀏覽器沒有。',
    keys: ['i · 檢視器    space/長按 · 望遠鏡', '滾輪、拖曳、↑↓ · 下行    home/end', 'm · 聲音    l · English    q · 手機'],
  },
};

export function fragmentAt(t) {
  let found = PASSAGE[0];
  for (const f of PASSAGE) {
    if (t >= f.at) found = f;
    else break;
  }
  return found;
}
